import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import useMediaQuery from '@mui/material/useMediaQuery';
import classnames from 'classnames';

import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import ArrowDownwardIcon from '@mui/icons-material/KeyboardArrowDown';

import { grey } from '@mui/material/colors';

import Spacings from '~/modules/ui-kit/spacings';
import UserListItem from '~/components/UserListItem';
import { useSettingsByCategory } from '~/hooks/api/useSettingsByCategory';
import styles from './styles.module.css';
import messages from './messages';

type Application = {
  id: number;
  publisher: {
    firstName: string;
    lastName: string;
    isShiftLeader: boolean;
    isVip: boolean;
    experienceLevel: string;
    congregation: {
      name: string;
    };
    email: string;
    languages: string[];
    phone: string;
    birthDate?: string;
    gender?: string;
    id: number;
  };
  hasVehicle: boolean;
  turn?: number | null;
  plannedTurn?: number | null;
};

export type Candidate = {
  application: Application;
  assignmentRatioNumerator: number;
  assignmentRatioDenominator: number;
  preference: number;
  assignment?: {
    isVisible: boolean;
    isLead: boolean;
  };
};

type UserListProp = {
  candidates: Candidate[];
  isCollapsible: boolean;
  isOpen: boolean;
  onToggle: () => void;
  title: ReactNode;
  setPublisherAsLead?: (candidate: { application: Application }) => void;
  renderListItemAction: (candidate: { application: Application }) => ReactNode;
  renderHeaderItemAction?: () => ReactNode;
};

export const UserList = (props: UserListProp) => {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const { data: settings } = useSettingsByCategory('planning');
  return (
    <div className={classnames({ [styles.open]: props.isOpen })}>
      <Spacings.Inset
        role="button"
        onClick={props.isCollapsible ? props.onToggle : undefined}
      >
        <Spacings.Inline alignItems="center">
          {props.isCollapsible && (
            <ArrowDownwardIcon
              style={{
                color: grey[500],
                transform: `rotate(${props.isOpen ? '180' : '0'}deg)`,
                transition: 'transform 300ms',
              }}
            />
          )}
          <Typography variant="subtitle2">
            {props.title} ({props.candidates.length})
          </Typography>
        </Spacings.Inline>
      </Spacings.Inset>
      {props.isOpen && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>
                <Typography variant="caption" color="textSecondary">
                  <FormattedMessage {...messages.publisher} />
                </Typography>
              </th>
              {isDesktop && (
                <th>
                  <Tooltip
                    title={
                      <FormattedMessage
                        {...messages.assignmentRatioExplanation}
                      />
                    }
                    arrow
                  >
                    <Typography variant="caption" color="textSecondary">
                      <FormattedMessage {...messages.assignmentRatio} />
                    </Typography>
                  </Tooltip>
                </th>
              )}
              {settings?.isVehicleWorkflowActive ? (
                <th>
                  <Typography variant="caption" color="textSecondary">
                    <FormattedMessage {...messages.car} />
                  </Typography>
                </th>
              ) : null}
              <th>
                <Typography variant="caption" color="textSecondary">
                  <FormattedMessage {...messages.experience} />
                </Typography>
              </th>
              <th>
                <Typography variant="caption" color="textSecondary">
                  <FormattedMessage {...messages.teamLead} />
                </Typography>
              </th>
              {isDesktop && (
                <>
                  <th>
                    <Typography variant="caption" color="textSecondary">
                      <FormattedMessage {...messages.vip} />
                    </Typography>
                  </th>
                  <th>
                    <Typography variant="caption" color="textSecondary">
                      <FormattedMessage {...messages.visible} />
                    </Typography>
                  </th>
                </>
              )}
              <th>{props.renderHeaderItemAction?.()}</th>
            </tr>
          </thead>
          <tbody>
            {props.candidates.map((candidate) => (
              <UserListItem
                key={candidate.application.id}
                {...candidate}
                setPublisherAsLead={
                  props.setPublisherAsLead
                    ? () => props.setPublisherAsLead?.(candidate)
                    : undefined
                }
                renderAction={() => props.renderListItemAction(candidate)}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default UserList;
